import { Button, Flex, Select, Space } from 'antd'
import { Plus, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { WorkspaceApp } from 'src/api/facade/types'
import { AppPermissionsSelect } from 'src/features/apps/components/AppPermissionsSelect'

import type { BindingDraft } from './types'

type Props = {
  apps: WorkspaceApp[]
  appsLoading?: boolean
  /** AntD `Form.Item` feeds these through value/onChange. */
  value?: BindingDraft[]
  onChange?: (value: BindingDraft[]) => void
}

/**
 * Controlled editor for a credential's app bindings. Each row picks a
 * workspace app and the permission codes that app grants the public token.
 * `app_code` / `saas_app_token` are resolved from the chosen app at submit
 * time (see the drawer), so the row only stores `appId` + permission codes.
 */
export const AppBindingsField = ({
  apps,
  appsLoading,
  value = [],
  onChange
}: Props) => {
  const { t } = useTranslation()

  const usedAppIds = value.map((b) => b.appId).filter(Boolean)

  const update = (index: number, next: BindingDraft) => {
    const copy = value.slice()
    copy[index] = next
    onChange?.(copy)
  }

  const remove = (index: number) =>
    onChange?.(value.filter((_, i) => i !== index))

  const add = () => onChange?.([...value, { appId: '', permission_codes: [] }])

  return (
    <Space vertical size={10} style={{ width: '100%' }}>
      {value.map((binding, index) => (
        <BindingRow
          // Each row owns a per-app permissions query, so key by the
          // selected app id (unique — used apps are disabled elsewhere) to keep
          // the right instance when a middle row is removed. Empty rows have no
          // loaded query state, so an index fallback is safe for them.
          key={binding.appId || `empty-${index}`}
          apps={apps}
          appsLoading={appsLoading}
          binding={binding}
          usedAppIds={usedAppIds}
          onChange={(next) => update(index, next)}
          onRemove={() => remove(index)}
        />
      ))}
      <Button
        type="dashed"
        size="small"
        icon={<Plus size={14} />}
        onClick={add}
        block
      >
        {t('workspace.publicAccess.bindings.addApp')}
      </Button>
    </Space>
  )
}

type BindingRowProps = {
  apps: WorkspaceApp[]
  appsLoading?: boolean
  binding: BindingDraft
  usedAppIds: string[]
  onChange: (next: BindingDraft) => void
  onRemove: () => void
}

const BindingRow = ({
  apps,
  appsLoading,
  binding,
  usedAppIds,
  onChange,
  onRemove
}: BindingRowProps) => {
  const { t } = useTranslation()

  const app = apps.find((a) => a.id === binding.appId)

  const appOptions = apps.map((a) => ({
    value: a.id,
    label: a.name,
    disabled: a.id !== binding.appId && usedAppIds.includes(a.id)
  }))

  return (
    <Flex
      gap={8}
      align="flex-start"
      style={{
        padding: 10,
        border: '1px solid var(--ant-color-border-secondary)',
        borderRadius: 8
      }}
    >
      <Space vertical size={8} style={{ flex: 1, minWidth: 0 }}>
        <Select
          style={{ width: '100%' }}
          loading={appsLoading}
          options={appOptions}
          value={binding.appId || undefined}
          placeholder={t('workspace.publicAccess.bindings.appPlaceholder')}
          showSearch={{ optionFilterProp: 'label' }}
          onChange={(appId) => onChange({ appId, permission_codes: [] })}
        />
        <AppPermissionsSelect
          app={app}
          value={binding.permission_codes}
          disabled={!binding.appId}
          placeholder={t(
            'workspace.publicAccess.bindings.permissionsPlaceholder'
          )}
          onChange={(permission_codes) =>
            onChange({ ...binding, permission_codes })
          }
        />
      </Space>
      <Button
        size="small"
        type="text"
        danger
        icon={<Trash2 size={14} />}
        onClick={onRemove}
        aria-label={t('workspace.publicAccess.bindings.removeApp')}
      />
    </Flex>
  )
}
